import { SITE_DESCRIPTION, SITE_NAME, siteUrl } from '@/lib/site-url';

/** Bump when the worker's cache strategy or precache list changes. */
export const PWA_CACHE_VERSION = 'ashkelon-news-pwa-v1';

export const PWA_MANIFEST_PATH = '/manifest.webmanifest';
export const PWA_SERVICE_WORKER_PATH = '/sw.js';
export const PWA_OFFLINE_PATH = '/offline.html';

export const PWA_THEME_COLOR = '#000000';
/** Same as `--background` / first-paint document color. */
export const PWA_BACKGROUND_COLOR = '#000000';

export type PwaIosStartupImage = {
    href: string;
    deviceWidth: number;
    deviceHeight: number;
    dpr: number;
    orientation: 'portrait' | 'landscape';
};

const PWA_IOS_STARTUP_DEVICES: Array<
    Omit<PwaIosStartupImage, 'href'>
> = [
    { deviceWidth: 430, deviceHeight: 932, dpr: 3, orientation: 'portrait' },
    { deviceWidth: 430, deviceHeight: 932, dpr: 3, orientation: 'landscape' },
    { deviceWidth: 393, deviceHeight: 852, dpr: 3, orientation: 'portrait' },
    { deviceWidth: 393, deviceHeight: 852, dpr: 3, orientation: 'landscape' },
    { deviceWidth: 390, deviceHeight: 844, dpr: 3, orientation: 'portrait' },
    { deviceWidth: 390, deviceHeight: 844, dpr: 3, orientation: 'landscape' },
    { deviceWidth: 414, deviceHeight: 896, dpr: 3, orientation: 'portrait' },
    { deviceWidth: 414, deviceHeight: 896, dpr: 3, orientation: 'landscape' },
    { deviceWidth: 375, deviceHeight: 812, dpr: 3, orientation: 'portrait' },
    { deviceWidth: 375, deviceHeight: 812, dpr: 3, orientation: 'landscape' },
    { deviceWidth: 414, deviceHeight: 896, dpr: 2, orientation: 'portrait' },
    { deviceWidth: 414, deviceHeight: 896, dpr: 2, orientation: 'landscape' },
    { deviceWidth: 375, deviceHeight: 667, dpr: 2, orientation: 'portrait' },
    { deviceWidth: 1024, deviceHeight: 1366, dpr: 2, orientation: 'portrait' },
    { deviceWidth: 834, deviceHeight: 1194, dpr: 2, orientation: 'portrait' },
    { deviceWidth: 768, deviceHeight: 1024, dpr: 2, orientation: 'portrait' },
];

export function pwaIosStartupImages(): PwaIosStartupImage[] {
    return PWA_IOS_STARTUP_DEVICES.map((device) => {
        const width =
            device.orientation === 'portrait'
                ? device.deviceWidth * device.dpr
                : device.deviceHeight * device.dpr;
        const height =
            device.orientation === 'portrait'
                ? device.deviceHeight * device.dpr
                : device.deviceWidth * device.dpr;
        return {
            ...device,
            href: `/pwa/splash-${width}x${height}.png`,
        };
    });
}

export function pwaIosStartupMedia(image: PwaIosStartupImage): string {
    return `(device-width: ${image.deviceWidth}px) and (device-height: ${image.deviceHeight}px) and (-webkit-device-pixel-ratio: ${image.dpr}) and (orientation: ${image.orientation})`;
}

/** Installed PWA window only. Regular browser tabs stay on `display-mode: browser`. */
export function isPwaStandaloneWindow(win: {
    navigator: { standalone?: boolean };
    matchMedia?: (query: string) => { matches: boolean };
}): boolean {
    if (win.navigator.standalone === true) return true;
    return win.matchMedia?.('(display-mode: standalone)').matches === true;
}

/** Runs before paint so a home-screen launch can show the splash without a white flash. */
export const PWA_SPLASH_BOOT_SCRIPT = `(function(){try{var p=location.pathname;if(p.indexOf('/admin')===0||p.indexOf('/sign-')===0||p==='/recover'||p.indexOf('/invite')===0)return;var standalone=window.navigator.standalone===true||(window.matchMedia&&window.matchMedia('(display-mode: standalone)').matches);if(standalone)document.documentElement.classList.add('pwa-standalone');}catch(e){}})();`;

export const PWA_SPLASH_CRITICAL_CSS = `#an-pwa-splash{display:none!important;position:fixed;inset:0;z-index:99999;align-items:center;justify-content:center;background:${PWA_BACKGROUND_COLOR};color:#fff;pointer-events:none}html.pwa-standalone:not(.pwa-splash-done) #an-pwa-splash{display:flex!important;pointer-events:auto}html.pwa-splash-done #an-pwa-splash{opacity:0;pointer-events:none;transition:opacity .4s ease}`;

/**
 * Same-origin paths the worker must not cache or rewrite.
 * Auth, admin, APIs, and tokenized links stay on the network.
 */
export const PWA_BYPASS_PREFIXES = [
    '/admin',
    '/api',
    '/sign-in',
    '/sign-up',
    '/sign-out',
    '/recover',
    '/invite',
    '/newsletter/verify',
    '/_server',
    '/_tanstack',
] as const;

export const PWA_PRECACHE_URLS = [
    PWA_OFFLINE_PATH,
    '/apple-icon.png',
    '/pwa-192.png',
    '/pwa-512.png',
    '/pwa-maskable-512.png',
    '/images/logo.svg',
] as const;

const ASSET_DESTINATIONS = new Set(['style', 'script', 'font', 'image', 'manifest']);

export function pathnameBypassesServiceWorker(pathname: string): boolean {
    const path = pathname.split('?')[0] || '/';
    if (path === PWA_SERVICE_WORKER_PATH) return true;
    return PWA_BYPASS_PREFIXES.some(
        (prefix) => path === prefix || path.startsWith(`${prefix}/`),
    );
}

export function shouldHandleFetch(input: {
    method: string;
    url: string;
    pageOrigin: string;
    destination?: string;
    mode?: string;
    hasRange?: boolean;
}): boolean {
    if (input.method.toUpperCase() !== 'GET') return false;
    if (input.hasRange) return false;
    let parsed: URL;
    try {
        parsed = new URL(input.url, input.pageOrigin);
    } catch {
        return false;
    }
    if (parsed.origin !== new URL(input.pageOrigin).origin) return false;
    if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return false;
    if (pathnameBypassesServiceWorker(parsed.pathname)) return false;
    if (
        input.mode === 'navigate'
        || input.destination === 'document'
        || parsed.pathname.startsWith('/assets/')
    ) {
        return true;
    }
    return Boolean(input.destination && ASSET_DESTINATIONS.has(input.destination));
}

/** Share-target and search box both land on `q`. */
export function shareTargetQuery(search: {
    q?: string;
    text?: string;
    url?: string;
}): string | undefined {
    const raw = search.q ?? search.text ?? search.url;
    const trimmed = raw?.trim();
    return trimmed || undefined;
}

export type WebAppManifest = {
    id: string;
    name: string;
    short_name: string;
    description: string;
    lang: string;
    dir: 'rtl';
    start_url: string;
    scope: string;
    display: 'standalone';
    display_override: string[];
    background_color: string;
    theme_color: string;
    orientation: 'any';
    categories: string[];
    icons: Array<{
        src: string;
        sizes: string;
        type: string;
        purpose?: string;
    }>;
    shortcuts: Array<{
        name: string;
        short_name: string;
        url: string;
        icons: Array<{ src: string; sizes: string; type: string }>;
    }>;
    share_target: {
        action: string;
        method: 'GET';
        enctype: 'application/x-www-form-urlencoded';
        params: { title: string; text: string; url: string };
    };
    handle_links: 'preferred';
    launch_handler: { client_mode: string[] };
    prefer_related_applications: false;
};

export function buildWebAppManifest(): WebAppManifest {
    const origin = siteUrl();
    const icon192 = { src: '/pwa-192.png', sizes: '192x192', type: 'image/png' };
    return {
        id: `${origin}/`,
        name: SITE_NAME,
        short_name: SITE_NAME,
        description: SITE_DESCRIPTION,
        lang: 'he',
        dir: 'rtl',
        start_url: '/',
        scope: '/',
        display: 'standalone',
        display_override: ['standalone', 'minimal-ui', 'browser'],
        background_color: PWA_BACKGROUND_COLOR,
        theme_color: PWA_THEME_COLOR,
        orientation: 'any',
        categories: ['news', 'magazines'],
        icons: [
            { ...icon192, purpose: 'any' },
            {
                src: '/pwa-512.png',
                sizes: '512x512',
                type: 'image/png',
                purpose: 'any',
            },
            {
                src: '/pwa-maskable-512.png',
                sizes: '512x512',
                type: 'image/png',
                purpose: 'maskable',
            },
        ],
        shortcuts: [
            {
                name: 'דף הבית',
                short_name: 'בית',
                url: '/',
                icons: [icon192],
            },
            {
                name: 'מזג האוויר',
                short_name: 'מזג אוויר',
                url: '/weather',
                icons: [icon192],
            },
            {
                name: 'חיפוש',
                short_name: 'חיפוש',
                url: '/search',
                icons: [icon192],
            },
            {
                name: 'שלחו ידיעה',
                short_name: 'ידיעה',
                url: '/tip',
                icons: [icon192],
            },
        ],
        share_target: {
            action: '/search',
            method: 'GET',
            enctype: 'application/x-www-form-urlencoded',
            params: {
                title: 'q',
                text: 'text',
                url: 'url',
            },
        },
        handle_links: 'preferred',
        launch_handler: { client_mode: ['navigate-existing', 'auto'] },
        prefer_related_applications: false,
    };
}

export function pwaDocumentHead() {
    return {
        meta: [
            { name: 'application-name', content: SITE_NAME },
            { name: 'apple-mobile-web-app-title', content: SITE_NAME },
            { name: 'apple-mobile-web-app-capable', content: 'yes' },
            { name: 'mobile-web-app-capable', content: 'yes' },
            { name: 'apple-mobile-web-app-status-bar-style', content: 'black-translucent' },
        ],
        links: [
            { rel: 'manifest', href: PWA_MANIFEST_PATH },
            ...pwaIosStartupImages().map((image) => ({
                rel: 'apple-touch-startup-image',
                href: image.href,
                media: pwaIosStartupMedia(image),
            })),
        ],
        scripts: [{ children: PWA_SPLASH_BOOT_SCRIPT }],
    };
}

export function buildServiceWorkerScript(): string {
    const cacheVersion = JSON.stringify(PWA_CACHE_VERSION);
    const offlinePath = JSON.stringify(PWA_OFFLINE_PATH);
    const bypass = JSON.stringify([...PWA_BYPASS_PREFIXES]);
    const precache = JSON.stringify([...PWA_PRECACHE_URLS]);
    const swPath = JSON.stringify(PWA_SERVICE_WORKER_PATH);

    return `/* Ashkelon News service worker. Generated from src/lib/pwa.ts. */
const CACHE_VERSION = ${cacheVersion};
const OFFLINE_URL = ${offlinePath};
const SW_PATH = ${swPath};
const BYPASS_PREFIXES = ${bypass};
const PRECACHE_URLS = ${precache};
const SHELL_CACHE = CACHE_VERSION + '-shell';
const PAGE_CACHE = CACHE_VERSION + '-pages';
const ASSET_CACHE = CACHE_VERSION + '-assets';
const PAGE_CACHE_LIMIT = 40;
const ASSET_CACHE_LIMIT = 80;
const ASSET_DESTINATIONS = ['style', 'script', 'font', 'image', 'manifest'];

function pathnameBypasses(pathname) {
  const path = pathname.split('?')[0] || '/';
  if (path === SW_PATH) return true;
  return BYPASS_PREFIXES.some(function (prefix) {
    return path === prefix || path.indexOf(prefix + '/') === 0;
  });
}

function trimCache(cacheName, maxEntries) {
  return caches.open(cacheName).then(function (cache) {
    return cache.keys().then(function (keys) {
      if (keys.length <= maxEntries) return;
      return cache.delete(keys[0]).then(function () {
        return trimCache(cacheName, maxEntries);
      });
    });
  });
}

self.addEventListener('install', function (event) {
  event.waitUntil(
    caches.open(SHELL_CACHE).then(function (cache) {
      return cache.addAll(PRECACHE_URLS);
    }).then(function () {
      return self.skipWaiting();
    }),
  );
});

self.addEventListener('activate', function (event) {
  event.waitUntil(
    caches.keys().then(function (keys) {
      return Promise.all(keys.filter(function (key) {
        return key.indexOf(CACHE_VERSION) !== 0;
      }).map(function (key) {
        return caches.delete(key);
      }));
    }).then(function () {
      return self.clients.claim();
    }),
  );
});

self.addEventListener('message', function (event) {
  if (event.data && event.data.type === 'SKIP_WAITING') {
    self.skipWaiting();
  }
});

function networkFirstPage(request) {
  return fetch(request).then(function (response) {
    if (response.ok && response.type === 'basic') {
      var copy = response.clone();
      caches.open(PAGE_CACHE).then(function (cache) {
        cache.put(request, copy);
        trimCache(PAGE_CACHE, PAGE_CACHE_LIMIT);
      });
    }
    return response;
  }).catch(function () {
    return caches.match(request).then(function (cached) {
      if (cached) return cached;
      return caches.match(OFFLINE_URL).then(function (offline) {
        return offline || new Response('אין חיבור לרשת.', {
          status: 503,
          headers: { 'Content-Type': 'text/plain; charset=utf-8' },
        });
      });
    });
  });
}

function cacheFirstAsset(request) {
  return caches.match(request).then(function (cached) {
    if (cached) return cached;
    return fetch(request).then(function (response) {
      if (response.ok && response.type === 'basic') {
        var copy = response.clone();
        caches.open(ASSET_CACHE).then(function (cache) {
          cache.put(request, copy);
          trimCache(ASSET_CACHE, ASSET_CACHE_LIMIT);
        });
      }
      return response;
    });
  });
}

self.addEventListener('fetch', function (event) {
  var request = event.request;
  if (request.method !== 'GET') return;
  if (request.headers.get('range')) return;
  var url;
  try {
    url = new URL(request.url);
  } catch (err) {
    return;
  }
  if (url.origin !== self.location.origin) return;
  if (pathnameBypasses(url.pathname)) return;

  var isDocument = request.mode === 'navigate' || request.destination === 'document';
  if (isDocument) {
    event.respondWith(networkFirstPage(request));
    return;
  }

  var isAsset = ASSET_DESTINATIONS.indexOf(request.destination) !== -1
    || url.pathname.indexOf('/assets/') === 0;
  if (isAsset) {
    event.respondWith(cacheFirstAsset(request));
  }
});
`;
}
